import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {Header} from 'types/payload-types';

/**
 * Custom hook that extracts and formats contact information from header data for the top bar component.
 * This hook processes tenant-specific organization metadata to provide properly formatted phone and email links.
 * It returns a standardized object containing all necessary contact information and external links for the top bar display.
 *
 * @param headerData The header configuration data containing tenant information and organization metadata.
 * @returns An object containing formatted contact information including phone, email, and external link details.
 *
 * @example
 * ```tsx
 * const headerData = {
 *   tenant: {
 *     organisationMetaData: {
 *       telephone: '+49 123 456789',
 *       email: 'info@example.com'
 *     },
 *     emailSettings: {
 *       emailSubject: 'Contact Request',
 *       emailBody: 'Hello, I would like to contact you.'
 *     }
 *   }
 * };
 *
 * const {
 *   phoneLabel,
 *   phoneHref,
 *   mailLabel,
 *   mailHref,
 *   linkLabel,
 *   linkHref
 * } = useTopBar(headerData);
 * ```
 */
export const useTopBar = (headerData: Header) => {
    let phoneLabel = '';
    let phoneHref = '';
    let mailLabel = '';
    let mailHref = '';
    const linkLabel = 'EIKACHIP Onlineshop';
    const linkHref = 'https://www.eikachip.de';

    if (isDefinedAndPopulated(headerData.tenant)) {
        const {emailSettings, organisationMetaData} = headerData.tenant;

        phoneLabel = organisationMetaData.telephone ?? '';
        phoneHref = organisationMetaData.telephone ? `tel:${organisationMetaData.telephone}` : '';

        mailLabel = organisationMetaData.email ?? '';
        mailHref = organisationMetaData.email ? `mailto:${organisationMetaData.email}?subject=${emailSettings?.emailSubject ?? ''}&body=${emailSettings?.emailBody ?? ''}` : '';
    }

    return {
        linkHref,
        linkLabel,
        mailHref,
        mailLabel,
        phoneHref,
        phoneLabel
    };
};